import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Plus as AddIcon } from 'lucide-react';
import { listUploadBatches, listUploadLinks, revokeUploadLink } from '../../utils/secureUploadApi';
import { HEAD_CELL_SX, TABLE_CONTAINER_SX } from '../../styles/tableStyles';

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const statusChip = (link) => {
  if (!link.is_active) return <Chip label="Revoked" color="default" size="small" />;
  if (link.is_expired) return <Chip label="Expired" color="warning" size="small" />;
  return <Chip label="Active" color="success" size="small" />;
};

const batchStatusChip = (status) => {
  const label = (status || 'uploaded')
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
  return <Chip label={label} color="primary" variant="outlined" size="small" />;
};

const submittedBy = (batch) => {
  if (batch.uploader_name && batch.uploader_email) {
    return `${batch.uploader_name} · ${batch.uploader_email}`;
  }
  return batch.uploader_name || batch.uploader_email || 'Not provided';
};

const UploadLinksPage = () => {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [copied, setCopied] = useState(null);

  const fetchLinks = async () => {
    try {
      const [linksResponse, batchesResponse] = await Promise.all([
        listUploadLinks(),
        listUploadBatches(),
      ]);
      setLinks(linksResponse.data);
      setBatches(batchesResponse.data);
    } catch (err) {
      console.error('Failed to load secure upload data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, []);

  const handleCopy = (link) => {
    navigator.clipboard.writeText(link.upload_url);
    setCopied(link.token);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleRevoke = async (token) => {
    setRevoking(token);
    try {
      await revokeUploadLink(token);
      await fetchLinks();
    } catch (err) {
      console.error('Failed to revoke link', err);
    } finally {
      setRevoking(null);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Upload Batches
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/upload-batches/new')}
        >
          Generate Link
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
            Generated Links
          </Typography>

          {links.length === 0 ? (
            <Box sx={{ textAlign: 'center', my: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No upload links yet. Generate one to get started.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={TABLE_CONTAINER_SX}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={HEAD_CELL_SX}>Provider</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Token</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Expires</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
                    <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'right' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {links.map((link) => (
                    <TableRow key={link.id}>
                      <TableCell>{link.provider_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={link.token.slice(0, 12) + '…'}
                          variant="outlined"
                          size="small"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      </TableCell>
                      <TableCell>{new Date(link.expires_at).toLocaleDateString()}</TableCell>
                      <TableCell>{statusChip(link)}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleCopy(link)}
                          >
                            {copied === link.token ? 'Copied!' : 'Copy link'}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={!link.is_active || link.is_expired || revoking === link.token}
                            onClick={() => handleRevoke(link.token)}
                          >
                            {revoking === link.token ? 'Revoking…' : 'Revoke'}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography variant="h6" component="h2" sx={{ mt: 4, mb: 1.5 }}>
            Submitted Batches
          </Typography>

          {batches.length === 0 ? (
            <Box sx={{ textAlign: 'center', my: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No submitted batches yet.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={TABLE_CONTAINER_SX}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={HEAD_CELL_SX}>Batch</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Provider</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Submitted by</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Files</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Submitted</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batches.map((batch) => (
                    <TableRow key={batch.id}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{batch.batch_ref}</TableCell>
                      <TableCell>{batch.provider_name}</TableCell>
                      <TableCell>{submittedBy(batch)}</TableCell>
                      <TableCell>
                        {batch.file_count} file{batch.file_count === 1 ? '' : 's'} · {formatBytes(batch.total_size_bytes)}
                      </TableCell>
                      <TableCell>{batchStatusChip(batch.status)}</TableCell>
                      <TableCell>{new Date(batch.submitted_at || batch.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Container>
  );
};

export default UploadLinksPage;
